import { Grid, Typography, Box, Paper, useTheme, alpha, InputLabel } from '@mui/material';
import React, { useMemo } from 'react';
import Checkbox from '@mui/material/Checkbox';
import CreateShipmentContext from '../../../context/CreateShipment';
import { createShipmentContextType, PackageType } from '../../../types/shipment';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';

const InsurancePackaging: React.FC = (): JSX.Element => {
  const theme = useTheme();
  const { packageData, setPackageData } = React.useContext(CreateShipmentContext) as createShipmentContextType;
  const shipmentSetting = useSelector((state: RootStateType) => state.orgSetup.shipmentSetting);
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationData);
  const { name = '' } = organizationData || {};

  const { insurance, packaging } = packageData;

  const disableInsurance = useMemo(
    () => !shipmentSetting?.insuranceCharge?.charge,
    [shipmentSetting?.insuranceCharge?.charge],
  );
  const disablePackaging = useMemo(
    () => !shipmentSetting?.packagingCharge?.charge,
    [shipmentSetting?.packagingCharge?.charge],
  );

  function handleChange(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
    const fieldName = event.target.name as 'insurance' | 'packaging';
    const packageDataTemp: PackageType = { ...packageData };
    if (['insurance', 'packaging'].includes(fieldName)) packageDataTemp[fieldName] = checked;
    setPackageData({ ...packageDataTemp });
  }

  if (name === 'Cargo Solutions Network') {
    return <></>;
  }

  return (
    <Box sx={{ mt: 2.5 }}>
      <InputLabel
        sx={{
          mb: '8px',
          color: 'common.black',
          fontSize: '14px',
          fontWeight: 500,
          textTransform: 'none',
          letterSpacing: '0.3px',
          display: 'block',
        }}
      >
        Value-Added Services
      </InputLabel>
      <Grid container spacing={2}>
        {/* Insurance Option Card */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={0}
            onClick={() => {
              if (!disableInsurance) {
                const syntheticEvent = {
                  target: { name: 'insurance' },
                } as React.ChangeEvent<HTMLInputElement>;
                handleChange(syntheticEvent, !insurance);
              }
            }}
            sx={{
              p: 1.5,
              borderRadius: '12px',
              border: `1.5px solid ${insurance ? theme.palette.primary.main : alpha(theme.palette.divider, 0.15)}`,
              bgcolor: insurance ? alpha(theme.palette.primary.main, 0.03) : theme.palette.background.paper,
              boxShadow: insurance
                ? `0 0 0 1px ${theme.palette.primary.main}, 0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}`
                : '0 1px 2px rgba(0,0,0,0.02)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: disableInsurance ? 'not-allowed' : 'pointer',
              opacity: disableInsurance ? 0.55 : 1,
              transition: 'all 0.2s ease',
              '&:hover': !disableInsurance
                ? {
                    borderColor: insurance ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.4),
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                  }
                : {},
            }}
          >
            <Checkbox
              name="insurance"
              id="insurance"
              checked={insurance}
              onChange={handleChange}
              disabled={disableInsurance}
              sx={{ p: 0.5 }}
            />
            <Box component="img" src="/images/Boxes/Insurance.svg" alt="logistics" sx={{ width: 22, height: 22 }} />
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: insurance ? 'primary.main' : 'text.primary',
                }}
              >
                Insure Your Shipment
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {disableInsurance ? 'Not available for this route' : 'Protect goods against transit loss'}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Packaging Option Card */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={0}
            onClick={() => {
              if (!disablePackaging) {
                const syntheticEvent = {
                  target: { name: 'packaging' },
                } as React.ChangeEvent<HTMLInputElement>;
                handleChange(syntheticEvent, !packaging);
              }
            }}
            sx={{
              p: 1.5,
              borderRadius: '12px',
              border: `1.5px solid ${packaging ? theme.palette.primary.main : alpha(theme.palette.divider, 0.15)}`,
              bgcolor: packaging ? alpha(theme.palette.primary.main, 0.03) : theme.palette.background.paper,
              boxShadow: packaging
                ? `0 0 0 1px ${theme.palette.primary.main}, 0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}`
                : '0 1px 2px rgba(0,0,0,0.02)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: disablePackaging ? 'not-allowed' : 'pointer',
              opacity: disablePackaging ? 0.55 : 1,
              transition: 'all 0.2s ease',
              '&:hover': !disablePackaging
                ? {
                    borderColor: packaging ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.4),
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                  }
                : {},
            }}
          >
            <Checkbox
              name="packaging"
              id="packaging"
              checked={packaging}
              onChange={handleChange}
              disabled={disablePackaging}
              sx={{ p: 0.5 }}
            />
            <Box component="img" src="/images/Boxes/Box.svg" alt="logistics" sx={{ width: 22, height: 22 }} />
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: packaging ? 'primary.main' : 'text.primary',
                }}
              >
                Add Packaging
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {disablePackaging ? 'Not available for this route' : 'Speedbox professional packaging materials'}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InsurancePackaging;
